import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { BlockPanel } from '../components/automations/BlockPanel';
import { Button, ConfirmDialog, ErrorNote, Loading, Modal, Toggle } from '../components/ui';
import { apiOrigin, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth-context-value';
import {
  BLOCK_META,
  BLOCK_ORDER,
  getLink,
  newBlock,
  newId,
  portsOf,
  setLink,
  summaryOf,
  unlinkTarget,
} from '../lib/automation-model';
import { formatDateTime } from '../lib/format';
import { automationsApi } from '../lib/resources';
import type {
  Automation,
  AutomationBlock,
  AutomationBlockType,
  AutomationNote,
} from '../types';

const CARD_WIDTH = 240;
const HEADER = 34;
const PORT_ROW = 24;
const LINE = 18;

interface View {
  x: number;
  y: number;
  zoom: number;
}

type Drag =
  | {
      kind: 'block';
      id: string;
      startX: number;
      startY: number;
      originX: number;
      originY: number;
    }
  | { kind: 'pan'; startX: number; startY: number; originX: number; originY: number }
  | { kind: 'link'; fromId: string; port: string };

const positionOf = (block: AutomationBlock) => block.presentation ?? { x: 0, y: 0 };

function cardHeight(block: AutomationBlock): number {
  const ports = portsOf(block).length;
  const lines = Math.min(summaryOf(block).length, 4);
  return HEADER + Math.max(ports * PORT_ROW, lines * LINE + 12) + 8;
}

const outPoint = (block: AutomationBlock, index: number) => {
  const { x, y } = positionOf(block);
  return { x: x + CARD_WIDTH, y: y + HEADER + index * PORT_ROW + PORT_ROW / 2 };
};
const inPoint = (block: AutomationBlock) => {
  const { x, y } = positionOf(block);
  return { x, y: y + HEADER / 2 };
};

function curve(from: { x: number; y: number }, to: { x: number; y: number }) {
  const dx = Math.max(40, Math.abs(to.x - from.x) / 2);
  return `M ${from.x} ${from.y} C ${from.x + dx} ${from.y}, ${to.x - dx} ${to.y}, ${to.x} ${to.y}`;
}

/** Editor da automação em canvas (Fase 4 / A3). Salvar grava uma nova versão. */
export function AutomationEditorPage() {
  const { id = '' } = useParams();
  const query = useQuery({
    queryKey: ['automation', id],
    queryFn: () => automationsApi.get(id),
  });
  if (query.isLoading) return <Loading />;
  if (query.isError || !query.data) {
    return (
      <div className="p-6">
        <ErrorNote>{errorMessage(query.error, 'Automação não encontrada')}</ErrorNote>
      </div>
    );
  }
  return <Editor key={query.data.id} automation={query.data} />;
}

function Editor({ automation }: { automation: Automation }) {
  const { can } = useAuth();
  const canEdit = can('flow_admin');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canvasRef = useRef<HTMLDivElement>(null);

  const [blocks, setBlocks] = useState<AutomationBlock[]>(automation.blocks);
  const [notes] = useState<AutomationNote[]>(automation.notes ?? []);
  const [name, setName] = useState(automation.name);
  const [selected, setSelected] = useState<string | null>(null);
  const [edge, setEdge] = useState<{ from: string; port: string } | null>(null);
  const [view, setView] = useState<View>({ x: 40, y: 40, zoom: 1 });
  const [drag, setDrag] = useState<Drag | null>(null);
  const [cursor, setCursor] = useState<{ x: number; y: number } | null>(null);
  const [showVersions, setShowVersions] = useState(false);
  const [showWebhook, setShowWebhook] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const dirty =
    name !== automation.name || JSON.stringify(blocks) !== JSON.stringify(automation.blocks);
  const byId = useMemo(() => new Map(blocks.map((block) => [block.id, block])), [blocks]);
  const selectedBlock = selected ? byId.get(selected) : undefined;

  // ------------------------------------------------------------ enquadrar
  const fit = useCallback(() => {
    const element = canvasRef.current;
    if (!element || !blocks.length) return;
    const xs = blocks.map((block) => positionOf(block).x);
    const ys = blocks.map((block) => positionOf(block).y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const maxX = Math.max(...xs) + CARD_WIDTH;
    const maxY = Math.max(...blocks.map((block) => positionOf(block).y + cardHeight(block)));
    const zoom = Math.max(
      0.25,
      Math.min(
        1,
        (element.clientWidth - 80) / (maxX - minX || 1),
        (element.clientHeight - 80) / (maxY - minY || 1),
      ),
    );
    setView({ zoom, x: 40 - minX * zoom, y: 40 - minY * zoom });
    // Só ao abrir: depois o usuário controla o enquadramento.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => fit(), [fit]);

  // --------------------------------------------------------- avisar ao sair
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const toWorld = useCallback(
    (clientX: number, clientY: number) => {
      const rect = canvasRef.current?.getBoundingClientRect();
      return {
        x: (clientX - (rect?.left ?? 0) - view.x) / view.zoom,
        y: (clientY - (rect?.top ?? 0) - view.y) / view.zoom,
      };
    },
    [view],
  );

  const updateBlock = (next: AutomationBlock) =>
    setBlocks((current) => current.map((block) => (block.id === next.id ? next : block)));

  const removeBlock = (blockId: string) => {
    setBlocks((current) =>
      unlinkTarget(
        current.filter((block) => block.id !== blockId),
        blockId,
      ),
    );
    setSelected(null);
  };

  const duplicateBlock = (block: AutomationBlock) => {
    const copy = JSON.parse(JSON.stringify(block)) as AutomationBlock;
    copy.id = newId();
    copy.presentation = { x: positionOf(block).x + 40, y: positionOf(block).y + 40 };
    for (const port of portsOf(copy)) Object.assign(copy, setLink(copy, port.key, ''));
    setBlocks((current) => [...current, copy]);
    setSelected(copy.id);
  };

  const addBlock = (type: AutomationBlockType) => {
    const element = canvasRef.current;
    const center = toWorld(
      (element?.getBoundingClientRect().left ?? 0) +
        (element?.clientWidth ?? 600) / 2 -
        CARD_WIDTH / 2,
      (element?.getBoundingClientRect().top ?? 0) + (element?.clientHeight ?? 400) / 2 - 40,
    );
    const block = newBlock(type, center.x, center.y);
    setBlocks((current) => [...current, block]);
    setSelected(block.id);
  };

  // ---------------------------------------------------------- mouse global
  useEffect(() => {
    if (!drag) return;
    const onMove = (event: MouseEvent) => {
      if (drag.kind === 'block') {
        const dx = (event.clientX - drag.startX) / view.zoom;
        const dy = (event.clientY - drag.startY) / view.zoom;
        setBlocks((current) =>
          current.map((block) =>
            block.id === drag.id
              ? {
                  ...block,
                  presentation: {
                    x: Math.round(drag.originX + dx),
                    y: Math.round(drag.originY + dy),
                  },
                }
              : block,
          ),
        );
      } else if (drag.kind === 'pan') {
        setView((current) => ({
          ...current,
          x: drag.originX + event.clientX - drag.startX,
          y: drag.originY + event.clientY - drag.startY,
        }));
      } else {
        setCursor(toWorld(event.clientX, event.clientY));
      }
    };
    const onUp = (event: MouseEvent) => {
      if (drag.kind === 'link') {
        const target = (
          document.elementFromPoint(event.clientX, event.clientY) as HTMLElement | null
        )
          ?.closest('[data-block-id]')
          ?.getAttribute('data-block-id');
        if (target && target !== drag.fromId) {
          setBlocks((current) =>
            current.map((block) =>
              block.id === drag.fromId ? setLink(block, drag.port, target) : block,
            ),
          );
        }
        setCursor(null);
      }
      setDrag(null);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [drag, view.zoom, toWorld]);

  // ------------------------------------------------------------- teclado
  useEffect(() => {
    if (!canEdit) return;
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable)
        return;
      if (event.key !== 'Delete' && event.key !== 'Backspace') return;
      if (edge) {
        setBlocks((current) =>
          current.map((block) =>
            block.id === edge.from ? setLink(block, edge.port, '') : block,
          ),
        );
        setEdge(null);
      } else if (selected) {
        removeBlock(selected);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  /** Zoom mantendo fixo o ponto (px, py) da tela do canvas. */
  const zoomAt = (factor: number, px?: number, py?: number) => {
    const element = canvasRef.current;
    const cx = px ?? (element?.clientWidth ?? 0) / 2;
    const cy = py ?? (element?.clientHeight ?? 0) / 2;
    setView((current) => {
      const zoom = Math.min(1.5, Math.max(0.25, current.zoom * factor));
      return {
        zoom,
        x: cx - ((cx - current.x) / current.zoom) * zoom,
        y: cy - ((cy - current.y) / current.zoom) * zoom,
      };
    });
  };

  const onWheel = (event: React.WheelEvent) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    zoomAt(event.deltaY > 0 ? 0.9 : 1.1, event.clientX - rect.left, event.clientY - rect.top);
  };

  // --------------------------------------------------------------- salvar
  const save = useMutation({
    mutationFn: () =>
      automationsApi.update(automation.id, {
        name: name.trim() || automation.name,
        blocks,
        notes,
      }),
    onSuccess: (saved) => {
      queryClient.setQueryData(['automation', automation.id], saved);
      void queryClient.invalidateQueries({ queryKey: ['automations'] });
      void queryClient.invalidateQueries({
        queryKey: ['automation', automation.id, 'versions'],
      });
    },
  });
  const toggle = useMutation({
    mutationFn: (active: boolean) => automationsApi.setActive(automation.id, active),
    onSuccess: (saved) => {
      queryClient.setQueryData(['automation', automation.id], saved);
      void queryClient.invalidateQueries({ queryKey: ['automations'] });
    },
  });

  const edges = blocks.flatMap((block) =>
    portsOf(block)
      .map((port, index) => ({
        block,
        port,
        index,
        target: byId.get(getLink(block, port.key)),
      }))
      .filter((entry) => entry.target),
  );
  const linkFrom = drag?.kind === 'link' ? byId.get(drag.fromId) : undefined;
  const linkIndex =
    drag?.kind === 'link' && linkFrom
      ? portsOf(linkFrom).findIndex((port) => port.key === drag.port)
      : -1;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Barra superior */}
      <header className="flex flex-wrap items-center gap-3 border-b border-slate-200 bg-white px-4 py-2">
        <button
          type="button"
          className="text-sm text-slate-500 hover:underline"
          onClick={() => (dirty ? setLeaving(true) : navigate('/automacoes'))}
        >
          ← Automações
        </button>
        <input
          className="min-w-0 flex-1 rounded border border-transparent px-2 py-1 text-lg font-semibold text-slate-900 hover:border-slate-200 focus:border-slate-300 focus:outline-none"
          value={name}
          disabled={!canEdit}
          maxLength={200}
          onChange={(event) => setName(event.target.value)}
        />
        <span className="text-xs text-slate-400">
          v{automation.version}
          {dirty && (
            <span className="ml-1 font-medium text-amber-600">· alterações não salvas</span>
          )}
        </span>
        <Toggle
          label={automation.active ? 'Ligada' : 'Desligada'}
          checked={automation.active}
          disabled={!canEdit || toggle.isPending || dirty}
          hint={dirty ? 'Salve antes' : undefined}
          onChange={(active) => toggle.mutate(active)}
        />
        <Button onClick={() => setShowWebhook(true)}>Webhook</Button>
        <Button onClick={() => setShowVersions(true)}>Versões</Button>
        <Link
          to={`/configuracoes/execucoes-automacao?automacao=${automation.id}`}
          className="text-sm text-slate-600 hover:underline"
        >
          Execuções
        </Link>
        {canEdit && (
          <>
            <Button
              disabled={!dirty || save.isPending}
              onClick={() => {
                setBlocks(automation.blocks);
                setName(automation.name);
                setSelected(null);
              }}
            >
              Descartar
            </Button>
            <Button
              variant="primary"
              disabled={!dirty || save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending ? 'Salvando…' : 'Salvar nova versão'}
            </Button>
          </>
        )}
      </header>
      {(save.isError || toggle.isError) && (
        <div className="border-b border-slate-200 bg-white px-4 py-2">
          <ErrorNote>{errorMessage(save.error ?? toggle.error)}</ErrorNote>
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        {/* Paleta */}
        {canEdit && (
          <aside className="w-44 shrink-0 space-y-1 overflow-y-auto border-r border-slate-200 bg-white p-2">
            <p className="px-1 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Adicionar bloco
            </p>
            {BLOCK_ORDER.map((type) => (
              <button
                key={type}
                type="button"
                title={BLOCK_META[type].hint}
                onClick={() => addBlock(type)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-50"
              >
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: BLOCK_META[type].color }}
                />
                {BLOCK_META[type].label}
              </button>
            ))}
          </aside>
        )}

        {/* Canvas */}
        <div
          ref={canvasRef}
          className="relative min-w-0 flex-1 cursor-grab overflow-hidden bg-slate-50 select-none"
          style={{
            backgroundImage: 'radial-gradient(circle, #cbd5e1 1px, transparent 1px)',
            backgroundSize: `${20 * view.zoom}px ${20 * view.zoom}px`,
            backgroundPosition: `${view.x}px ${view.y}px`,
          }}
          onWheel={onWheel}
          onMouseDown={(event) => {
            if (event.target !== event.currentTarget) return;
            setSelected(null);
            setEdge(null);
            setDrag({
              kind: 'pan',
              startX: event.clientX,
              startY: event.clientY,
              originX: view.x,
              originY: view.y,
            });
          }}
        >
          <div
            className="absolute left-0 top-0"
            style={{
              transform: `translate(${view.x}px, ${view.y}px) scale(${view.zoom})`,
              transformOrigin: '0 0',
            }}
          >
            <svg
              className="pointer-events-none absolute left-0 top-0 overflow-visible"
              width={1}
              height={1}
            >
              {edges.map(({ block, port, index, target }) => {
                const path = curve(outPoint(block, index), inPoint(target!));
                const active = edge?.from === block.id && edge.port === port.key;
                return (
                  <g
                    key={`${block.id}-${port.key}`}
                    className="pointer-events-auto cursor-pointer"
                  >
                    <path
                      d={path}
                      stroke="transparent"
                      strokeWidth={14}
                      fill="none"
                      onMouseDown={(event) => {
                        event.stopPropagation();
                        setEdge({ from: block.id, port: port.key });
                        setSelected(null);
                      }}
                    />
                    <path
                      d={path}
                      stroke={active ? '#0f172a' : port.color}
                      strokeWidth={active ? 3 : 2}
                      fill="none"
                      markerEnd="url(#arrow)"
                    />
                  </g>
                );
              })}
              {linkFrom && cursor && linkIndex >= 0 && (
                <path
                  d={curve(outPoint(linkFrom, linkIndex), cursor)}
                  stroke="#0f172a"
                  strokeDasharray="6 4"
                  strokeWidth={2}
                  fill="none"
                />
              )}
              <defs>
                <marker
                  id="arrow"
                  viewBox="0 0 10 10"
                  refX="9"
                  refY="5"
                  markerWidth="7"
                  markerHeight="7"
                  orient="auto-start-reverse"
                >
                  <path d="M 0 0 L 10 5 L 0 10 z" fill="#64748b" />
                </marker>
              </defs>
            </svg>

            {notes.map((note, index) => (
              <div
                key={note.id ?? index}
                className="absolute w-56 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900 shadow-sm"
                style={{ left: note.presentation?.x ?? 0, top: note.presentation?.y ?? 0 }}
              >
                {String(note.text ?? note.content ?? '')}
              </div>
            ))}

            {blocks.map((block) => {
              const meta = BLOCK_META[block.type] ?? { label: block.type, color: '#94a3b8' };
              const ports = portsOf(block);
              const lines = summaryOf(block);
              const counters = automation.counters?.[block.id];
              const { x, y } = positionOf(block);
              return (
                <div
                  key={block.id}
                  data-block-id={block.id}
                  className={`absolute rounded-xl border bg-white shadow-sm ${
                    selected === block.id
                      ? 'border-slate-900 ring-2 ring-slate-900/10'
                      : 'border-slate-200'
                  }`}
                  style={{ left: x, top: y, width: CARD_WIDTH, minHeight: cardHeight(block) }}
                  onMouseDown={(event) => {
                    event.stopPropagation();
                    setSelected(block.id);
                    setEdge(null);
                    if (canEdit) {
                      setDrag({
                        kind: 'block',
                        id: block.id,
                        startX: event.clientX,
                        startY: event.clientY,
                        originX: x,
                        originY: y,
                      });
                    }
                  }}
                >
                  {/* entrada */}
                  {block.type !== 'trigger' && (
                    <span
                      className="absolute -left-1.5 h-3 w-3 rounded-full border-2 border-white bg-slate-400"
                      style={{ top: HEADER / 2 - 6 }}
                    />
                  )}
                  <div
                    className="flex items-center justify-between rounded-t-xl px-3 text-xs font-semibold text-white"
                    style={{ height: HEADER, backgroundColor: meta.color }}
                  >
                    <span>{meta.label}</span>
                    {counters && (
                      <span className="font-normal opacity-90">
                        {counters.success}✓{counters.alert ? ` ${counters.alert}!` : ''}
                        {counters.error ? ` ${counters.error}✕` : ''}
                      </span>
                    )}
                  </div>
                  <div className="space-y-0.5 py-1.5 pl-3 pr-24 text-xs text-slate-600">
                    {lines.slice(0, 4).map((line, index) => (
                      <p key={index} className="truncate" style={{ lineHeight: `${LINE}px` }}>
                        {line}
                      </p>
                    ))}
                    {lines.length > 4 && <p className="text-slate-400">+{lines.length - 4}</p>}
                  </div>
                  {/* saídas */}
                  {ports.map((port, index) => (
                    <div
                      key={port.key}
                      className="absolute right-0 flex items-center gap-1 text-[10px] text-slate-500"
                      style={{ top: HEADER + index * PORT_ROW, height: PORT_ROW }}
                    >
                      <span className="max-w-20 truncate">{port.label}</span>
                      <span
                        className={`-mr-1.5 h-3 w-3 rounded-full border-2 border-white ${canEdit ? 'cursor-crosshair' : ''}`}
                        style={{ backgroundColor: port.color }}
                        onMouseDown={(event) => {
                          event.stopPropagation();
                          if (!canEdit) return;
                          setDrag({ kind: 'link', fromId: block.id, port: port.key });
                          setCursor(toWorld(event.clientX, event.clientY));
                        }}
                      />
                    </div>
                  ))}
                </div>
              );
            })}
          </div>

          {/* controles */}
          <div className="absolute bottom-3 left-3 flex gap-1 rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
            <button
              type="button"
              className="px-2 text-sm text-slate-600 hover:bg-slate-100"
              onClick={() => zoomAt(0.85)}
            >
              −
            </button>
            <span className="px-1 text-xs leading-6 text-slate-500">
              {Math.round(view.zoom * 100)}%
            </span>
            <button
              type="button"
              className="px-2 text-sm text-slate-600 hover:bg-slate-100"
              onClick={() => zoomAt(1.15)}
            >
              +
            </button>
            <button
              type="button"
              className="px-2 text-xs text-slate-600 hover:bg-slate-100"
              onClick={fit}
            >
              Enquadrar
            </button>
          </div>
          {edge && canEdit && (
            <div className="absolute bottom-3 right-3 flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs shadow-sm">
              Ligação selecionada
              <Button
                variant="danger"
                onClick={() => {
                  setBlocks((current) =>
                    current.map((block) =>
                      block.id === edge.from ? setLink(block, edge.port, '') : block,
                    ),
                  );
                  setEdge(null);
                }}
              >
                Remover
              </Button>
            </div>
          )}
          {!blocks.length && (
            <p className="absolute inset-0 flex items-center justify-center text-sm text-slate-400">
              Adicione um bloco de gatilho pela paleta.
            </p>
          )}
        </div>

        {/* Painel lateral */}
        {selectedBlock && (
          <aside className="w-80 shrink-0 border-l border-slate-200 bg-white">
            <BlockPanel
              block={selectedBlock}
              blocks={blocks}
              canEdit={canEdit}
              counters={automation.counters?.[selectedBlock.id]}
              onChange={updateBlock}
              onDelete={() => removeBlock(selectedBlock.id)}
              onDuplicate={() => duplicateBlock(selectedBlock)}
            />
          </aside>
        )}
      </div>

      {showVersions && (
        <VersionsModal automation={automation} onClose={() => setShowVersions(false)} />
      )}
      {showWebhook && (
        <WebhookModal automation={automation} onClose={() => setShowWebhook(false)} />
      )}
      {leaving && (
        <ConfirmDialog
          title="Sair sem salvar?"
          message="As alterações do fluxo serão perdidas."
          confirmLabel="Sair sem salvar"
          danger
          onConfirm={() => navigate('/automacoes')}
          onClose={() => setLeaving(false)}
        />
      )}
    </div>
  );
}

function VersionsModal({
  automation,
  onClose,
}: {
  automation: Automation;
  onClose: () => void;
}) {
  const versions = useQuery({
    queryKey: ['automation', automation.id, 'versions'],
    queryFn: () => automationsApi.versions(automation.id),
  });
  return (
    <Modal title="Versões" onClose={onClose}>
      <div className="space-y-2 text-sm text-slate-600">
        <p>
          Atual: <strong>v{automation.version}</strong> · {formatDateTime(automation.updatedAt)}
        </p>
        {versions.data?.length ? (
          <ul className="max-h-72 space-y-1 overflow-y-auto">
            {versions.data.map((version) => (
              <li key={version.id}>
                v{version.version} — {version.name} · {formatDateTime(version.createdAt)}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-slate-400">
            Nenhuma versão anterior — cada salvamento do fluxo guarda uma.
          </p>
        )}
        <div className="flex justify-end">
          <Button onClick={onClose}>Fechar</Button>
        </div>
      </div>
    </Modal>
  );
}

function WebhookModal({
  automation,
  onClose,
}: {
  automation: Automation;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const rotate = useMutation({
    mutationFn: () => automationsApi.rotateWebhook(automation.id),
    onSuccess: (saved) => {
      queryClient.setQueryData(['automation', automation.id], saved);
      setConfirming(false);
    },
  });
  const url = automation.webhookUrl ? `${apiOrigin()}${automation.webhookUrl}` : null;
  return (
    <Modal title="Webhook de entrada" onClose={onClose}>
      <div className="space-y-3 text-sm text-slate-600">
        {url ? (
          <>
            <p>
              Para o gatilho "Requisição HTTP": envie um <code>POST</code> com JSON para a URL
              abaixo. O corpo vira a fonte de dados do gatilho.
            </p>
            <code className="block break-all rounded bg-slate-50 p-2 text-xs text-slate-700">
              {url}
            </code>
            {rotate.isError && <ErrorNote>{errorMessage(rotate.error)}</ErrorNote>}
            <div className="flex justify-end gap-2">
              <Button onClick={() => setConfirming(true)}>Gerar nova URL</Button>
              <Button
                variant="primary"
                onClick={() => {
                  void navigator.clipboard.writeText(url);
                  setCopied(true);
                }}
              >
                {copied ? 'Copiado' : 'Copiar'}
              </Button>
            </div>
          </>
        ) : (
          <p>Só quem administra automações vê a URL do webhook.</p>
        )}
      </div>
      {confirming && (
        <ConfirmDialog
          title="Gerar nova URL"
          message="A URL atual para de funcionar na hora. Quem envia para ela precisa ser atualizado."
          confirmLabel="Gerar nova URL"
          danger
          busy={rotate.isPending}
          onConfirm={() => rotate.mutate()}
          onClose={() => setConfirming(false)}
        />
      )}
    </Modal>
  );
}
